/**
 * useAuth - Redux-based auth hook
 * Same API as old AuthContext - drop-in replacement for fast Redux reads
 * Permissions are stored normalized (lowercase, spaces/underscores → hyphens);
 * checks normalize the input so e.g. agentCallReport-list matches stored agentcallreport-list.
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginUser,
  logoutUser,
  refreshPermissions,
  checkAuth,
  impersonateUser,
  leaveImpersonation,
} from '../store/slices/authSlice';

/** Must match authSlice normalization so checks align with stored permissions */
const normalizePermission = (name) =>
  (name || '').toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');

/** Backend may send "Agent Call Report List" → agent-call-report-list; we check agentCallReport-list → agentcallreport-list */
const PERMISSION_ALIASES = {
  'agentcallreport-list': ['agent-call-report-list'],
  'agentcallreport-create': ['agent-call-report-create'],
  'agentcallreport-edit': ['agent-call-report-edit'],
  'agentcallreport-view': ['agent-call-report-view'],
  'agentcallreport-delete': ['agent-call-report-delete'],
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const { user, permissions, isAuthenticated, loading, permissionsReady, impersonating } =
    useSelector((state) => state.auth);

  const isSuperAdmin = permissions.some(
    (p) => normalizePermission(p) === 'super-admin'
  );

  const hasPermission = useCallback(
    (perm) => {
      if (!perm) return false;
      const normalized = normalizePermission(perm);
      if (isSuperAdmin) return true;
      if (permissions.some((p) => normalizePermission(p) === normalized)) return true;
      const aliases = PERMISSION_ALIASES[normalized];
      return Array.isArray(aliases) && aliases.some((a) => permissions.includes(a));
    },
    [permissions, isSuperAdmin]
  );
  const hasAnyPermission = useCallback(
    (perms) => isSuperAdmin || (Array.isArray(perms) && perms.some((p) => hasPermission(p))),
    [isSuperAdmin, hasPermission]
  );

  const login = useCallback(
    (credentials) => dispatch(loginUser(credentials)),
    [dispatch]
  );

  const logout = useCallback(() => dispatch(logoutUser()), [dispatch]);

  const refreshPermissionsFn = useCallback(
    () => dispatch(refreshPermissions()),
    [dispatch]
  );

  const initAuth = useCallback(() => dispatch(checkAuth()), [dispatch]);

  const impersonate = useCallback(
    (userId) => dispatch(impersonateUser(userId)),
    [dispatch]
  );

  const leaveImpersonationFn = useCallback(
    () => dispatch(leaveImpersonation()),
    [dispatch]
  );

  return {
    user,
    permissions,
    isAuthenticated,
    loading,
    permissionsReady,
    impersonating: !!impersonating,
    login,
    logout,
    refreshPermissions: refreshPermissionsFn,
    hasPermission,
    hasAnyPermission,
    initAuth,
    impersonate,
    leaveImpersonation: leaveImpersonationFn,
  };
};
